import { useState, useRef, useEffect } from 'react'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  FolderInput,
  FolderPlus,
  Copy,
  ExternalLink,
  X,
  Check,
  Loader2,
} from 'lucide-react'
import { cn } from '../lib/utils'

export function ContextMenuButton({ children, onClick, className }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(e)
      }}
      className={cn(
        "p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-accent transition-all",
        className
      )}
    >
      {children}
    </button>
  )
}

export function ContextMenu({ isOpen, onClose, position, children }) {
  const menuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose()
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[180px] bg-popover border border-border rounded-lg shadow-lg py-1 animate-in fade-in-0 zoom-in-95"
      style={{ top: position.y, left: position.x }}
    >
      {children}
    </div>
  )
}

export function ContextMenuItem({ icon: Icon, label, onClick, destructive, disabled }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        if (!disabled) onClick?.(e)
      }}
      disabled={disabled}
      className={cn(
        "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left",
        destructive && "text-destructive hover:bg-destructive/10",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </button>
  )
}

export function ContextMenuSeparator() {
  return <div className="h-px bg-border my-1" />
}

// Rename Dialog
export function RenameDialog({ isOpen, onClose, onSubmit, initialValue, title = "Rename" }) {
  const [value, setValue] = useState(initialValue || '')
  const [loading, setLoading] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue || '')
      setTimeout(() => inputRef.current?.select(), 50)
    }
  }, [isOpen, initialValue])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!value.trim() || value === initialValue) return
    setLoading(true)
    try {
      await onSubmit(value.trim())
      onClose()
    } catch (err) {
      console.error('Rename error:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] bg-card rounded-xl shadow-2xl z-50 p-6">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-muted border-0 focus:ring-2 focus:ring-primary/20 outline-none"
            placeholder="Enter name..."
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !value.trim() || value === initialValue}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Save
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// Confirm Delete Dialog
export function ConfirmDialog({ isOpen, onClose, onConfirm, title, message, confirmLabel = "Delete", destructive = true }) {
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await onConfirm()
      onClose()
    } catch (err) {
      console.error('Confirm error:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] bg-card rounded-xl shadow-2xl z-50 p-6">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-muted-foreground mb-6">{message}</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={cn(
              "px-4 py-2 rounded-lg transition-colors flex items-center gap-2",
              destructive
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : "bg-primary text-primary-foreground hover:bg-primary/90",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  )
}

// Move to Space Dialog
export function MoveToSpaceDialog({ isOpen, onClose, onSubmit, spaces, currentSpaceId }) {
  const [selectedSpaceId, setSelectedSpaceId] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setSelectedSpaceId(null)
    }
  }, [isOpen])

  const handleSubmit = async () => {
    if (selectedSpaceId === null) return
    setLoading(true)
    try {
      await onSubmit(selectedSpaceId)
      onClose()
    } catch (err) {
      console.error('Move error:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] max-h-[500px] bg-card rounded-xl shadow-2xl z-50 flex flex-col">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold">Move to Space</h3>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <div className="space-y-1">
            {spaces.map((space) => (
              <button
                key={space.id}
                onClick={() => setSelectedSpaceId(space.id)}
                disabled={space.id === currentSpaceId}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors",
                  selectedSpaceId === space.id
                    ? "bg-primary/10 text-primary border border-primary/30"
                    : "hover:bg-accent",
                  space.id === currentSpaceId && "opacity-50 cursor-not-allowed"
                )}
              >
                <FolderInput className="w-4 h-4" />
                <span className="flex-1 truncate">{space.name}</span>
                {space.id === currentSpaceId && (
                  <span className="text-xs text-muted-foreground">(current)</span>
                )}
                {selectedSpaceId === space.id && (
                  <Check className="w-4 h-4 text-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || selectedSpaceId === null}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Move
          </button>
        </div>
      </div>
    </>
  )
}

// Create Space Dialog
export function CreateSpaceDialog({ isOpen, onClose, onSubmit, parentId = null }) {
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      setName('')
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return
    setLoading(true)
    try {
      await onSubmit(name.trim(), parentId)
      onClose()
    } catch (err) {
      console.error('Create space error:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] bg-card rounded-xl shadow-2xl z-50 p-6">
        <h3 className="text-lg font-semibold mb-4">Create New Space</h3>
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-muted border-0 focus:ring-2 focus:ring-primary/20 outline-none"
            placeholder="Space name..."
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name.trim()}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
